import { styled } from '@mui/material/styles';
import { FC, useMemo, useState } from 'react';
import { SortOrder } from 'further-types/utils';

import Table from 'further-ui/components/Table';
import TabsPanel from 'further-ui/components/TabsPanel';

import { Row } from 'components/Layout';
import ReviewPayments from 'components/ReviewPayments';
import Chip from 'components/Chip';
import TransactionAllocator from 'components/TransactionAllocator';
import { useLayoutContext } from 'contexts/LayoutContext';
import { ActionOperationType } from 'further-types/lp/action';
import { IncomeOrExpenseType } from 'further-types/lp/income-and-expenses';
import { usePagination } from 'further-ui/hooks';
import { ActionRefLabels, IncomeAndExpensesLabels } from 'further-ui/labels';
import { CalendarDay, numberToCurrencyString } from 'further-ui/utils';
import useTransactionTypeSelectorOptions from 'hooks/form/useTransactionTypeSelectorOptions';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import useVehicle from 'hooks/lp/data/vehicle/useVehicle';
import api from 'lib/trpcClient';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { createLpPeriodEndReviewDetailRoute } from 'adminConstants/routes';
import BankReconciliationWarningBox from '../BankReconciliationWarning';
import ProcessingPage from 'components/new/ProcessingPage';
import { Button, CircularProgress, Typography } from '@mui/material';
import FullPageDrawer from 'components/new/FullPageDrawer';
import { Column } from 'further-ui/components/Layout';

enum ReviewAccrualsTab {
  PayableExpenses = 'payableExpenses',
  ReceivableIncome = 'receivableIncome',
  AccruedExpenses = 'accruedExpenses',
  AccruedIncome = 'accruedIncome',
}

const tabs = [
  { key: ReviewAccrualsTab.PayableExpenses, title: 'Payables accruals' },
  { key: ReviewAccrualsTab.ReceivableIncome, title: 'Receivables accruals' },
  { key: ReviewAccrualsTab.AccruedExpenses, title: 'Accrued expenses' },
  { key: ReviewAccrualsTab.AccruedIncome, title: 'Accrued income' },
];

const TabLabelWrapper = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

const ExpandedRowContainer = styled('div')(({ theme }) => ({
  backgroundColor: theme.palette.grey[50],
  borderTop: `1px solid ${theme.palette.grey[300]}`,
}));

const isPayableReceivableTab = (tab: ReviewAccrualsTab) =>
  tab === ReviewAccrualsTab.PayableExpenses ||
  tab === ReviewAccrualsTab.ReceivableIncome;

const pageId = 'review-accruals';

type Props = {
  onExit: () => void;
};

const ReviewAccruals: FC<Props> = ({ onExit }) => {
  const { selectedVehicleId } = useLayoutContext();
  const { vehicle } = useVehicle(selectedVehicleId);
  const { search } = useLocation();
  const { periodId } = useParams<{ periodId?: string }>();
  const searchParams = new URLSearchParams(search);
  const periodStartParam = searchParams.get('periodStart');
  const periodEndParam = searchParams.get('periodEnd');
  const periodStart = periodStartParam
    ? new CalendarDay(periodStartParam)
    : null;
  const periodEnd = periodEndParam ? new CalendarDay(periodEndParam) : null;
  const history = useHistory();
  const [isProcessingApproval, setIsProcessingApproval] = useState(false);
  const handleMutation = useHandleTrpcMutation<string>();
  const confirmChecklistItems =
    api.accountingPeriod.confirmChecklistItems.useMutation(
      handleMutation({ refetchType: 'active' }),
    );
  const handleExit = () => {
    onExit();
  };

  const handleApprove = () => {
    if (!selectedVehicleId || !periodId) return;
    setIsProcessingApproval(true);
    confirmChecklistItems.mutate(
      {
        vehicleId: selectedVehicleId,
        id: periodId,
        items: ['accrualsPayablesReceivables'],
        confirmed: true,
      },
      {
        onSuccess: () =>
          history.push(createLpPeriodEndReviewDetailRoute(periodId)),
        onError: () => setIsProcessingApproval(false),
      },
    );
  };
  const [activeTab, setActiveTab] = useState<ReviewAccrualsTab>(
    ReviewAccrualsTab.PayableExpenses,
  );
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const pagination = usePagination({
    id: pageId,
    order: SortOrder.Desc,
    orderBy: 'transactionDate',
  });

  const { transactionTypeOptions, isLoading: transactionTypesLoading } =
    useTransactionTypeSelectorOptions();

  const isPayableReceivable = isPayableReceivableTab(activeTab);
  const queryEnabled = !!periodStart && !!periodEnd && !!selectedVehicleId;
  const periodParams = {
    vehicleId: selectedVehicleId ?? '',
    periodStart: periodStart ?? undefined,
    periodEnd: periodEnd ?? undefined,
    page: pagination.page,
    rowsPerPage: pagination.rowsPerPage,
    order: pagination.order,
    orderBy: pagination.orderBy,
  };

  const { data: payableExpensesData, isFetching: payableExpensesLoading } =
    api.prepayments.getAccruals.useQuery(
      { ...periodParams, actionOperationType: ActionOperationType.Spent },
      { enabled: queryEnabled },
    );

  const { data: receivableIncomeData, isFetching: receivableIncomeLoading } =
    api.prepayments.getAccruals.useQuery(
      { ...periodParams, actionOperationType: ActionOperationType.Received },
      { enabled: queryEnabled },
    );

  const { data: accruedExpensesData, isFetching: accruedExpensesLoading } =
    api.incomeAndExpenses.getAccruedItems.useQuery(
      { ...periodParams, category: IncomeOrExpenseType.Expense },
      { enabled: queryEnabled },
    );

  const { data: accruedIncomeData, isFetching: accruedIncomeLoading } =
    api.incomeAndExpenses.getAccruedItems.useQuery(
      { ...periodParams, category: IncomeOrExpenseType.Income },
      { enabled: queryEnabled },
    );

  const tabData = {
    [ReviewAccrualsTab.PayableExpenses]: payableExpensesData,
    [ReviewAccrualsTab.ReceivableIncome]: receivableIncomeData,
    [ReviewAccrualsTab.AccruedExpenses]: accruedExpensesData,
    [ReviewAccrualsTab.AccruedIncome]: accruedIncomeData,
  };

  const tabCounts = {
    [ReviewAccrualsTab.PayableExpenses]: payableExpensesData?.totalCount ?? 0,
    [ReviewAccrualsTab.ReceivableIncome]: receivableIncomeData?.totalCount ?? 0,
    [ReviewAccrualsTab.AccruedExpenses]: accruedExpensesData?.totalCount ?? 0,
    [ReviewAccrualsTab.AccruedIncome]: accruedIncomeData?.totalCount ?? 0,
  };

  const tabLoading = {
    [ReviewAccrualsTab.PayableExpenses]: payableExpensesLoading,
    [ReviewAccrualsTab.ReceivableIncome]: receivableIncomeLoading,
    [ReviewAccrualsTab.AccruedExpenses]: accruedExpensesLoading,
    [ReviewAccrualsTab.AccruedIncome]: accruedIncomeLoading,
  };

  const data = tabData[activeTab];

  const handleTabChange = (tab: ReviewAccrualsTab) => {
    setActiveTab(tab);
    setExpandedRowId(null);
    pagination.handleChangeSort('transactionDate', SortOrder.Desc);
  };

  const rowsForTable = (data?.results ?? []).map((row) => ({
    ...row,
    id: row.id ?? '',
  }));

  const payableReceivableColumns = [
    {
      key: 'actionRef',
      label: 'Action',
      render: (row) => ActionRefLabels[row.actionRef],
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'transactionDate',
      label: 'Date',
      render: (row) => row.transactionDate.toLabel(),
    },
    {
      key: 'amountTcy',
      label: 'Amount currently accrued (TCY)',
      render: (row) =>
        numberToCurrencyString(row.unreconciledAmountTcy, {
          fallback: '-',
          currency: row.currencyCode,
        }),
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount currently accrued (LCY)',
      render: (row) =>
        numberToCurrencyString(row.unreconciledAmountLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      disableSorting: true,
    },
  ];

  const accruedColumns = [
    {
      key: 'incomeOrExpenseType',
      label: 'Type',
      render: (row) =>
        IncomeAndExpensesLabels[row.incomeOrExpenseType] ??
        row.incomeOrExpenseType,
      disableSorting: true,
    },
    {
      key: 'description',
      label: 'Description',
      disableSorting: true,
    },
    {
      key: 'transactionDate',
      label: 'Date',
      render: (row) => row.transactionDate.toLabel(),
    },
    {
      key: 'amountTcy',
      label: 'Amount currently accrued (TCY)',
      render: (row) =>
        numberToCurrencyString(row.unreconciledAmountTcy, {
          fallback: '-',
          currency: row.currencyCode,
        }),
      disableSorting: true,
    },
    {
      key: 'amountLcy',
      label: 'Amount currently accrued (LCY)',
      render: (row) =>
        numberToCurrencyString(row.unreconciledAmountLcy, {
          fallback: '-',
          currency: vehicle?.baseCurrency,
        }),
      disableSorting: true,
    },
    {
      key: 'counterpartyLabel',
      label: 'Counterparty',
      disableSorting: true,
    },
  ];

  const columns = useMemo(
    () => (isPayableReceivable ? payableReceivableColumns : accruedColumns),
    [isPayableReceivable, vehicle?.baseCurrency],
  );

  const mainTable = (
    <Table
      columns={columns}
      rows={rowsForTable}
      pagination={{
        page: pagination.page,
        rowsPerPage: pagination.rowsPerPage,
        order: pagination.order,
        orderBy: pagination.orderBy,
        handleRequestSort: pagination.handleRequestSort,
        handleChangePage: pagination.handleChangePage,
        handleChangeRowsPerPage: pagination.handleChangeRowsPerPage,
      }}
      rowsTotalCount={data?.totalCount ?? 0}
      isExpandableRow={() => true}
      isExpandedRow={(row) => expandedRowId === row.id}
      onRowExpand={(row) =>
        setExpandedRowId((prev) => (prev === row.id ? null : row.id))
      }
      getExpandedContent={(row) => (
        <ExpandedRowContainer>
          {isPayableReceivable ? (
            <TabsPanel
              tabs={[
                {
                  key: 'accountTransactions',
                  label: 'Account transactions',
                  content: (
                    <Row fullWidth>
                      {!transactionTypesLoading && vehicle && (
                        <TransactionAllocator
                          showCurrencyRate
                          transactionTypeOptions={transactionTypeOptions}
                          transactions={row.transactions}
                          readOnly
                          onChange={() => {}}
                          transactionCurrency={row.currencyCode}
                          vehicle={vehicle}
                          fxRate={row.currencyRate}
                          darkBackground
                        />
                      )}
                    </Row>
                  ),
                },
                {
                  key: 'paymentRecord',
                  label: 'Payment record',
                  content: (
                    <Row fullWidth>
                      <ReviewPayments
                        transactionId={row.id}
                        vehicleId={selectedVehicleId ?? ''}
                        currencyCode={row.currencyCode}
                        amountTcy={row.amountTcy}
                      />
                    </Row>
                  ),
                },
              ]}
            />
          ) : (
            <Row fullWidth>
              {!transactionTypesLoading && vehicle && (
                <TransactionAllocator
                  showCurrencyRate
                  transactionTypeOptions={transactionTypeOptions}
                  transactions={row.transactions}
                  readOnly
                  onChange={() => {}}
                  transactionCurrency={row.currencyCode}
                  vehicle={vehicle}
                  fxRate={row.currencyRate}
                  darkBackground
                />
              )}
            </Row>
          )}
        </ExpandedRowContainer>
      )}
    />
  );

  return (
    <FullPageDrawer
      open
      size="lg"
      innerScroll
      onClose={handleExit}
      title="Outstanding accruals, payables and receivables"
      actions={
        <Row fullWidth justify="space-between">
          <Button size="large" variant="outlined" onClick={handleExit}>
            Exit
          </Button>
          {periodId && (
            <Button size="large" variant="contained" onClick={handleApprove}>
              Approve and mark as reviewed
            </Button>
          )}
        </Row>
      }
    >
      <Column spacing="md" fullWidth>
        {isProcessingApproval ? (
          <ProcessingPage
            title="Approving transactions"
            subtitle="This will only take a moment."
          />
        ) : (
          <>
            <Typography variant="h5">Review transactions</Typography>
            <Typography variant="body2">
              Review and approve outstanding accruals, payables and receivables
              until {periodEnd?.toReadable()}.
            </Typography>
            <TabsPanel
              tabs={tabs.map(({ key, title }) => ({
                key,
                label: (
                  <TabLabelWrapper>
                    {title}
                    {tabLoading[key] ? (
                      <CircularProgress size={16} />
                    ) : (
                      <Chip
                        variant="tabIndicator"
                        small
                        bold={false}
                        empty={tabCounts[key] === 0}
                      >
                        {tabCounts[key]}
                      </Chip>
                    )}
                  </TabLabelWrapper>
                ),
                content: (
                  <>
                    {isPayableReceivableTab(key) && (
                      <BankReconciliationWarningBox
                        date={periodStart?.toUTCDate() ?? null}
                        warningText="There are pending transactions awaiting bank reconciliation. Click below to process these before reviewing accruals, as reconciled transactions may change outstanding accruals."
                      />
                    )}
                    {mainTable}
                  </>
                ),
              }))}
              onTabChange={handleTabChange}
              defaultTab={ReviewAccrualsTab.PayableExpenses}
            />
          </>
        )}
      </Column>
    </FullPageDrawer>
  );
};

export default ReviewAccruals;
